'use client'

import { Box } from '@mui/joy'
import UIFlex from '@components/ui/UIFlex'
import { SeeAllButton } from '@components/button/SeeAllButton'
import { pxToRem } from '@ui/style/muiTheme'
import color from '@ui/style/color.theme'
import ContentCard, { ContentCardProps } from '@components/content/ContentCard'
import { ArticleIssueTimeLineResponseVo, ArticleListResponseVo } from '@schemas/non-auth'
import { getRelativeTime } from '@utils/date'
import UILink from '@components/ui/UILink'
import SubSectionTitle from '@page/article/_components/common/SubSectionTitle'
import IssueTimelineItem from './IssueTimelineItem'
import { getArticleLink } from '@utils/getLink'
import { scrollToTopAndEnableAutoScrollRestoration } from '@utils/scroll'

export interface IssueTimelineProps {
	issueTimeLine?: ArticleIssueTimeLineResponseVo
	areaIdx?: number
}
const IssueTimeline = ({ issueTimeLine, areaIdx }: IssueTimelineProps) => {
	const { articleIssueTimeLinePackageTypeCode: typeCode } = issueTimeLine ?? {}
	const isDisplay = typeCode && ['ISSUE', 'TOPIC'].includes(typeCode)
	if (!issueTimeLine || !typeCode || !isDisplay) {
		return null
	}
	const isTopic = typeCode === 'TOPIC'
	const timelineType = isTopic ? 'topic' : 'issues'
	const cardProps: Omit<ContentCardProps, 'content'> = {
		infoConfig: {
			titleClamp: 2,
			infoGap: 0,
			infoSx: {
				flexDirection: 'column-reverse',
				'.info-title': {
					fontSize: pxToRem(15),
					lineHeight: pxToRem(22),
					marginTop: pxToRem(8),
				},
				'.info-additionalBox': {
					marginTop: 0,
				},
			},
		},
	}
	const drawIssueTimeLine = (issue: ArticleListResponseVo, index: number) => (
		<IssueTimelineItem
			key={`issue-timeline-${issue.articleIdx}`}
			{...(index === 0 && { isFirst: true })}
			{...(index === (issueTimeLine?.articleIssueTimeLineList?.length ?? 0) - 1 && {
				isLast: true,
			})}
		>
			<ContentCard
				content={{
					title: issue.articleTitle,
					href: `${getArticleLink(issue, 'ARTICLE', areaIdx)}&code=${
						issueTimeLine.articleIssueTimeLinePackageTypeCode
					}&idx=${issueTimeLine.articleIssueTimeLinePackageIdx}`,
					alt: '',
					additionalText: getRelativeTime(issue.publicationDate),
					onClick: scrollToTopAndEnableAutoScrollRestoration,
				}}
				infoConfig={cardProps.infoConfig}
			/>
		</IssueTimelineItem>
	)
	return (
		issueTimeLine.articleIssueTimeLineList.length > 0 && (
			<Box>
				{issueTimeLine?.articleIssueTimeLineName && (
					<UILink
						href={`/${timelineType}/${
							issueTimeLine?.articleIssueTimeLinePackageIdx ?? ''
						}`}
						onClick={scrollToTopAndEnableAutoScrollRestoration}
					>
						<SubSectionTitle>{issueTimeLine?.articleIssueTimeLineName}</SubSectionTitle>
					</UILink>
				)}
				<UIFlex
					padding={`${pxToRem(20)} ${pxToRem(24)} ${pxToRem(20)} ${pxToRem(18)}`}
					borderRadius={pxToRem(8)}
					sx={{
						background: color.colGray1,
						'&>*:not(:first-child)': {
							marginTop: pxToRem(8),
						},
					}}
				>
					<Box>
						{issueTimeLine?.articleIssueTimeLineList
							?.slice(0, 5)
							?.map(drawIssueTimeLine)}
					</Box>
					<UIFlex alignItems="center">
						<UILink
							href={`/${timelineType}/${
								issueTimeLine?.articleIssueTimeLinePackageIdx ?? ''
							}`}
							onClick={scrollToTopAndEnableAutoScrollRestoration}
						>
							<SeeAllButton
								title={isTopic ? '토픽' : '이슈'}
								tabIndex={-1}
								sx={{
									'&.MuiButton-root': {
										background: `${color.colWhite}`,
									},
								}}
							/>
						</UILink>
					</UIFlex>
				</UIFlex>
			</Box>
		)
	)
}

export default IssueTimeline
